import projectModel from '$$projectModel';
import helper from './helpers';

export default {
  valuesLoaded: state => state.valuesLoaded,
  valuesLoadedByProjectId: (state, getters) => projectId => {
    const project = getters.getProjectById(projectId);
    let valuesLoaded = false;

    if (!project) return valuesLoaded;
    if (project.values) {
      valuesLoaded = true;
    }

    return valuesLoaded;
  },
  getAllCustomColumns: state => state?.columns,
  getStaticColumns: state => state.staticColumns,
  getProjectColumns: state => state?.projectColumns,
  getJiraColumns: state => (Array.isArray(state?.jiraColumns) ? state.jiraColumns : []),
  getCustomColumnById: state => id => state.columnsMap[+id] || null,
  getCustomColumnByIdFromMap: state => id => state.columnsMap[id] || null,
  getColumnIdsByProjectId: state => projectId => state.projectColumns.find(project => project.id === +projectId)?.columns || [],
  getColumnByName: (state, getters) => (columnName, ganttId = null) => {
    const activeProjectData = ganttId ? projectModel.getItem(ganttId) : projectModel.getActiveProjectData();

    let columns = [];

    if (activeProjectData && activeProjectData.is_jira) {
      columns = getters.getJiraColumnsByProjectId(activeProjectData.gantt_id);
    } else {
      columns = state.staticColumns;
    }

    return columns.find(col => col.name === columnName) || null;
  },
  getJiraColumnsByProjectId: (state, getters) => projectId => {
    const jiraColumns = getters.getJiraColumns.find(col => +col.id === +projectId)?.fields || [];

    return helper.prepareJiraOptions(jiraColumns);
  },
  getJiraColumnByNameAndProjectId: (state, getters) => (name, projectId) => {
    const jiraColumns = getters.getJiraColumnsByProjectId(projectId);
    const column = jiraColumns.find(col => col.name === name);

    return column || null;
  },
  getProjectById: state => projectId => state.projectColumns.find(project => +project.id === +projectId),
  getProjectColumnsByIds: state => projectIds => {
    if (!Array.isArray(projectIds) || !projectIds.length) {
      return [];
    }

    const normalizedIds = new Set(projectIds.map(id => +id).filter(id => Number.isFinite(id)));

    return state.projectColumns.filter(project => normalizedIds.has(+project.id));
  },
  getProjectsByColumnId: state => columnId => {
    const projectsIds = [];
    const normalizedColumnId = +columnId;

    state.projectColumns.forEach(project => {
      const hasColumn = project?.columns?.some(id => +id === normalizedColumnId);

      if (!hasColumn || project.is_archived) return;

      projectsIds.push(+project.id);
    });

    return projectsIds;
  },

  // return object of "[taskId-columnId]: value" pairs
  getValuesMapByProjectId: (state, getters) => projectId => {
    const project = getters.getProjectById(projectId);
    const columnsIds = getters.getColumnIdsByProjectId(projectId);
    const columnsMap = {};

    columnsIds.forEach(columnId => {
      columnsMap[columnId] = getters.getCustomColumnById(columnId);
    });

    if (!project) return {};

    const valuesMap = {};

    if (!project.values) return valuesMap;

    Object.keys(project.values).forEach(taskId => {
      const customColumnValues = project.values[taskId];

      customColumnValues.forEach(customColumnValueObject => {
        const column = columnsMap[customColumnValueObject.column_id];
        const isOptionsTypeField = column
          && (column.type === 7 || column.type === 8 || column.type === 9);

        if (isOptionsTypeField) {
          if (valuesMap[`${taskId}-${customColumnValueObject.column_id}`]) {
            valuesMap[`${taskId}-${customColumnValueObject.column_id}`].push(customColumnValueObject.task_value);
          } else {
            valuesMap[`${taskId}-${customColumnValueObject.column_id}`] = [customColumnValueObject.task_value];
          }
        } else {
          valuesMap[`${taskId}-${customColumnValueObject.column_id}`] = customColumnValueObject.task_value;
        }
      });
    });

    return valuesMap;
  },
  // return object of "[taskId-columnId]: value" pairs
  getValuesMapByProjectIdForBoard: (state, getters) => projectId => {
    const project = getters.getProjectById(projectId);
    const columnsIds = getters.getColumnIdsByProjectId(projectId);
    const columnsMap = {};

    columnsIds.forEach(columnId => {
      columnsMap[columnId] = getters.getCustomColumnById(columnId);
    });

    if (!project) return {};

    const valuesMap = {};

    if (!project.values) return valuesMap;
    
    Object.keys(project.values).forEach(taskId => {
      const customColumnValues = project.values[taskId];

      customColumnValues.forEach(customColumnValueObject => {
        const column = columnsMap[customColumnValueObject.column_id];

        if (!column) return;

        let isOptionsTypeField = [1, 6, 7, 8, 9].includes(column.type);

        if (isOptionsTypeField) {
          if (column.options.findIndex(opt => opt.id === +customColumnValueObject.task_value) > -1) {
            (valuesMap[`${taskId}-${customColumnValueObject.column_id}`] ??= []).push(customColumnValueObject.task_value);
          }
        } else {
          valuesMap[`${taskId}-${customColumnValueObject.column_id}`] = customColumnValueObject.task_value;
        }
      });
    });

    return valuesMap;
  },
  getValuesMapForTask: (state, getters) => (projectId, taskId) => {
    const project = getters.getProjectById(projectId);
    const columnsIds = getters.getColumnIdsByProjectId(projectId);
    const columnsMap = {};

    columnsIds.forEach(columnId => {
      columnsMap[columnId] = getters.getCustomColumnById(columnId);
    });

    if (!project) return {};
    const valuesMap = {};

    const customColumnValues = project.values?.[taskId];

    if (customColumnValues) {
      customColumnValues.forEach(customColumnValueObject => {
        const column = columnsMap[customColumnValueObject.column_id];
        const isOptionsTypeField = column
          && (column.type === 7 || column.type === 8 || column.type === 9);

        if (isOptionsTypeField) {
          if (valuesMap[customColumnValueObject.column_id]) {
            valuesMap[customColumnValueObject.column_id].push(customColumnValueObject.task_value);
          } else {
            valuesMap[customColumnValueObject.column_id] = [customColumnValueObject.task_value];
          }
        } else {
          valuesMap[customColumnValueObject.column_id] = customColumnValueObject.task_value;
        }
      });
    }

    return valuesMap;
  },
  // getValueByParams: (state, getters) => params => {
  //   const { columnId, taskId, projectId } = params;
  //   let values = {};
  //   const project = getters.getProjectById(projectId);

  //   if (!project?.columns.find(id => id === columnId)) return [];

  //   const column = getters.getCustomColumnById(columnId);
  //   let isOptionsTypeField = [1, 6, 7, 8, 9].includes(column.type);

  //   if (project && project.values) values = project.values;
  //   if (taskId && values[taskId]) values = values[taskId];
  //   if (columnId) values = _.filter(values, o => {
  //     let additionalCondition = true;

  //     if (isOptionsTypeField) {
  //       additionalCondition = column.options.findIndex(opt => opt.id === +o.task_value) > -1;
  //     }

  //     return o.column_id === +columnId && additionalCondition;
  //   });

  //   return values;
  // },
  getValuesByParams: (state, getters) => params => {
    const { columnId, taskId, projectId } = params;
    const project = getters.getProjectById(projectId);
    let result = [];

    if (!project?.columns.find(id => id === columnId)) return [];

    if (project && project.values) {
      result = project.values[taskId] || [];
    }

    return result.filter(o => o.column_id === columnId);
  },
  getProjectsNumberByColumnId: state => columnId => {
    const projectNum = {
      visible: 0,
      archive: 0,
    };
    const normalizedColumnId = +columnId;

    state.projectColumns.forEach(project => {
      const isColumn = project.columns?.some(id => +id === normalizedColumnId);

      if (isColumn) {
        project.is_archived ? ++projectNum.archive : ++projectNum.visible;
      }
    });

    return projectNum;
  },
  isOptionsTypeField: (state, getters) => columnId => {
    const column = getters.getCustomColumnById(columnId);

    return column?.type === 7 || column?.type === 8 || column?.type === 9;
  },
};
